import { StringParseError } from '@amazon/vinyl-util'

export function parseMrn(mrn: string): MrnV1 {
    return parseMrnV1(mrn)
}

/**
 * A signed-in customer's purchased / saved entity carries a `library`-authority
 * MRN (e.g. `mrn:1.0:library:album:asin:B0…`). The cloud queue routes those to
 * the owned-content provider (`FixedOrderQueueContentProviderService`), which
 * rejects `InitiateQueue` for a catalog-backed purchase. When the library entity
 * is catalog-backed — its identifier is an `asin` — play it from the `catalog`
 * authority so the streaming provider serves it, mirroring GH (which always
 * queues catalog MRNs). True library-only content (an uploaded track keyed by
 * `objectid`, with no catalog asin) is left on the library authority so it still
 * routes to the owned-content provider. Non-MRN ids (bare asins) pass through.
 */
export function catalogizeLibraryAsinMrn(id: string): string {
    const libraryPrefix = `mrn:1.0:${MrnAuthority.LIBRARY}:`
    if (!id.startsWith(libraryPrefix)) return id
    // Guard on the name portion only — the `?territory=…` query must not be
    // mistaken for the identifier-type segment.
    const name = id.split('?', 1)[0]
    if (!name.includes(`:${MrnIdentifierType.ASIN}:`)) return id
    return `mrn:1.0:${MrnAuthority.CATALOG}:` + id.slice(libraryPrefix.length)
}

/**
 * This function deserializes a given mrn string to mrn {@link MrnV1} object.
 *
 * @param mrn the given mrn string
 * @returns {@link MrnV1} object
 */
function parseMrnV1(mrn: string): MrnV1 {
    const mrnPrefix = `mrn:1.0:`
    if (!mrn.includes(mrnPrefix)) {
        throw new StringParseError(
            mrn,
            0,
            `Invalid MRN string. Expected prefix ${mrnPrefix}. Valid MRN requires prefix containing mrn scheme and version.`
        )
    }
    const attributes: Record<string, string> = {}
    let components = [mrn]
    if (mrn.includes('?')) {
        components = mrn.split('?')
        const parameters = components[1]
        const attributesParts = parameters.split('&')
        for (const part of attributesParts) {
            const [key, value] = part.split('=')
            if (!key || !value) {
                throw new StringParseError(
                    mrn,
                    0,
                    `Invalid MRN string. When reading parameters ${parameters}, unexpected attribute ${part} was found. Expected the form ...?key=value&...`
                )
            }
            attributes[key.toLowerCase().trim()] = value.trim()
        }
    }
    const name = components[0].substring(mrnPrefix.length)
    const nameSegments = name.split(':')
    if (nameSegments.length < 4) {
        throw new StringParseError(
            mrn,
            0,
            `Invalid MRN string. Wrong number of segments in name: Has ${nameSegments.length}, but expected >= 4.`
        )
    }
    const authority = nameSegments[0].toLowerCase().trim() as MrnAuthority
    const mediaType = nameSegments[1].toLowerCase().trim() as MrnMediaType
    const mediaIdentifierType = nameSegments[2].toLowerCase().trim() as MrnIdentifierType
    const identifier = nameSegments[3].trim()
    return {
        version: '1.0',
        authority,
        mediaType,
        mediaIdentifierType,
        attributes,
        identifier,
    }
}

export type Mrn = MrnV1

export type MrnV1 = {
    readonly version: '1.0'
    readonly authority: MrnAuthority
    readonly mediaType: MrnMediaType
    readonly mediaIdentifierType: MrnIdentifierType
    readonly identifier: string
    readonly attributes?: {
        readonly [key: string]: string
    }
}

export enum MrnMediaType {
    /**
     * Indicates the item represents an artist entity.
     */
    ARTIST = 'artist',

    /**
     * Indicates the item represents a track entity.
     */
    MUSIC_TRACK = 'track',

    /**
     * Indicates the item represents an album entity.
     */
    MUSIC_ALBUM = 'album',

    /**
     * Indicates the item represents a playlist entity.
     */
    PLAYLIST = 'playlist',

    /**
     * A curated station that is part of the catalog.
     */
    STATION = 'station',

    /**
     * An entity representing a media queue.
     */
    QUEUE_ID = 'queueid',

    /**
     * Identifies a genre by its identifier such as *7wFG4piZ*.
     * Please see commissioner for an interface.
     * This identifier is most-useful as a facet.
     * Such a facet would typically arise on APIs for recommendation or search systems.
     */
    GENRE = 'genre',

    /**
     * Identifies an era by decade such as *1990*.
     * Please see commissioner for an interface.
     * This identifier is most-useful as a facet.
     * Such a facet would typically arise on APIs for recommendation or search systems.
     */
    ERA = 'era',

    /**
     * Identifies a mood by its identifier such as *kAHwHoME*.
     * Please see commissioner for an interface.
     * This identifier is most-useful as a facet.
     * Such a facet would typically arise on APIs for recommendation or search systems.
     */
    MOOD = 'mood',

    /**
     * Identifies an activity by its identifier such as *0IVLOHs7*.
     * Please see commissioner for an interface.
     * This identifier is most-useful as a facet.
     * Such a facet would typically arise on APIs for recommendation or search systems.
     */
    ACTIVITY = 'activity',

    /**
     * Identifies a tag used to mark content curated for a specific experience, or purpose. It is used for both
     * inclusive and exclusive purposes.
     * For example, *xXJCvzGN* identifies content as being the "best content" to recommend in the "play music" use
     * case as chosen by the curation team.
     * This identifier is most-useful as a facet.
     * Such a facet would typically arise on APIs for recommendation or search systems.
     */
    INTERNAL_TAG = 'internal',

    /**
     * Identifies a tag marking content curated for a specific event: holidays, and other celebrated editorial themes
     * (i.e. women’s day *ACJQTKYI*, Valentine’s Day *ZL12Y8WQ*).
     * This identifier is most-useful as a facet.
     * Such a facet would typically arise on APIs for recommendation or search systems.
     */
    THEME = 'theme',

    /**
     * An entity representing a podcast show.
     */
    SHOW = 'show',

    /**
     * An entity representing a podcast episode.
     */
    EPISODE = 'episode',

    /**
     * An entity representing a videostory.
     */
    VIDEOSTORY = 'videostory',

    /**
     * An entity representing a video.
     */
    VIDEO = 'video',
}

export enum MrnIdentifierType {
    /**
     * Container identifier that is an aggregate of the items identified by ASIN in the list.
     * This type is used when the media type is MUSIC_TRACK_LIST.
     */
    ASIN_LIST = 'asins',

    /**
     * An arbitrary (non-ASIN, non-UUID) unique identifier generated for a playlist
     */
    PLAYLIST_ID = 'playlistid',

    /**
     * A global ASIN representing the identifier for a media item or container in the catalog.
     */
    ASIN = 'asin',

    /**
     * A global titleset ASIN representing the identifier for a media item or container in the catalog.
     * This entity is effectively a grouping of entities. These entities are effectively "the same thing".
     * The metadata for this is the "best representative" of those independent entities.
     */
    TITLESET_ASIN = 'titlesetasin',

    /**
     * A unique identifier for a library track. It can also be stored locally and is
     * referred to as a local unique ID (LUID) on the app.
     */
    OBJECT_ID = 'objectid',

    /**
     * An identifier for a catalog station.
     */
    STATION_KEY = 'key',

    /**
     * An identifier representing an algorithmic station. In Amazon Music, this is a
     * Stations For Anything (SFA) type of station that is seeded from one or more entities.
     */
    STATION_SEED_ID = 'seedid',

    /**
     * UUID
     */
    UUID = 'uuid',

    /**
     * Identifies a tag from the set of Genres, Eras, Moods, Activities, Internal curation, or Themes, etc.
     */
    TAG = 'tag',
}

/**
 * Enum defining values of Mrn Authority Type.
 */
export enum MrnAuthority {
    /**
     * Stored in the catalog (includes curated content)
     */
    CATALOG = 'catalog',

    /**
     * Stored in the customer's library
     */
    LIBRARY = 'library',

    /**
     * Derived algorithmically
     */
    ALGORITHMIC = 'algorithmic',

    /**
     * Stored in a queue
     */
    QUEUE = 'queue',

    /**
     * Stored in the podcast catalog
     */
    PODCAST = 'podcast',
}
